import React, { useMemo } from 'react';
import { Typography, Space, Card, Row, Col, Flex, Grid, Tooltip } from 'antd';
import { FilterOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import SearchBox from '../SearchBox';
import Button from '../Button';
import SwitchComponent from '../SwitchComponent';
import styles from './PageWrapper.module.scss';

const { Title } = Typography;

const FilterIconWithIndicator: React.FC<{ hasActiveFilters: boolean }> = ({ hasActiveFilters }) => {
  return (
    <div className={styles.filterIconContainer}>
      <FilterOutlined />
      {hasActiveFilters && <div className={styles.filterIndicator} />}
    </div>
  );
};

interface ActionButton {
  title?: string;
  icon?: React.ReactNode;
  permission?: boolean;
  onClick?: () => void;
  theme?: 'primary' | 'secondary' | 'danger';
  type?: 'primary' | 'default' | 'dashed' | 'link' | 'text' | 'switch';
  disabled?: boolean;
  hasActiveFilters?: boolean;
  tooltip?: string;
  // Switch specific props
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  label?: string;
}

interface PageWrapperProps {
  children: React.ReactNode;
  title: string;
  subtitle?: string;
  actionButtons?: ActionButton[];
  search?: boolean;
  searchValue?: string;
  searchPlaceholder?: string;
  onSearch?: (value: string) => void;
  titleTooltip?: string;
}

const PageWrapper: React.FC<PageWrapperProps> = ({
  children,
  title,
  subtitle,
  actionButtons = [],
  search = false,
  searchValue,
  searchPlaceholder = 'Search',
  onSearch,
  titleTooltip,
}) => {
  const screens = Grid.useBreakpoint();

  const processedActionButtons = useMemo(() => {
    return actionButtons
      .filter((button) => button.permission !== false)
      .map((button) => {
        if (button.type === 'switch') {
          return button;
        }
        const isFilterButton = button.title?.toLowerCase().includes('filter');
        const iconToUse =
          isFilterButton && button.hasActiveFilters ? <FilterIconWithIndicator hasActiveFilters={button.hasActiveFilters} /> : button.icon;

        return {
          ...button,
          icon: iconToUse,
        };
      });
  }, [actionButtons]);

  return (
    <div>
      <Space
        direction="vertical"
        size="large"
        style={{ width: '100%' }}
      >
        <div>
          <Card
            style={{
              background: 'transparent',
              width: '100%',
              border: 'none',
            }}
            bodyStyle={{ padding: 0 }}
          >
            <Row
              gutter={[16, 16]}
              justify="space-between"
              align="middle"
            >
              <Col
                xs={24}
                sm={24}
                md={12}
                lg={12}
                xl={12}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Title
                      level={2}
                      style={{
                        margin: 0,
                        fontSize: '24px',
                      }}
                    >
                      {title}
                    </Title>
                    {titleTooltip && (
                      <Tooltip title={titleTooltip}>
                        <QuestionCircleOutlined
                          style={{
                            color: '#a4a7ae',
                            fontSize: '16px',
                            cursor: 'pointer',
                          }}
                        />
                      </Tooltip>
                    )}
                  </div>
                  {subtitle && <Typography.Text type="secondary">{subtitle}</Typography.Text>}
                </div>
              </Col>
              <Col
                xs={24}
                sm={24}
                md={12}
                lg={12}
                xl={12}
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }}
              >
                <Flex
                  gap="8px"
                  align="center"
                  wrap={!screens.md}
                >
                  {search && (
                    <SearchBox
                      placeholder={searchPlaceholder}
                      value={searchValue}
                      onSearch={onSearch}
                      style={{ width: '300px', minWidth: '250px' }}
                    />
                  )}
                  {processedActionButtons.map((button, index) => {
                    if (button.type === 'switch') {
                      const switchElement = (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <SwitchComponent
                            theme="primary"
                            checked={button.checked}
                            onChange={button.onChange}
                            disabled={button.disabled}
                          />
                          {button.label && (
                            <label
                              style={{
                                fontSize: '14px',
                                fontWeight: 500,
                                color: 'var(--color-text-secondary)',
                                margin: 0,
                              }}
                            >
                              {button.label}
                            </label>
                          )}
                        </div>
                      );

                      return <React.Fragment key={index}>{switchElement}</React.Fragment>;
                    }

                    const buttonElement = (
                      <Button
                        theme={button.theme || 'primary'}
                        type={button.type}
                        icon={button.icon}
                        onClick={button.onClick}
                        disabled={button.disabled}
                        style={{ width: 'auto' }}
                      >
                        {button.title}
                      </Button>
                    );

                    return button.tooltip ? (
                      <Tooltip
                        key={index}
                        title={button.tooltip}
                        classNames={{ root: 'custom-tooltip new-tooltip' }}
                      >
                        {buttonElement}
                      </Tooltip>
                    ) : (
                      <React.Fragment key={index}>{buttonElement}</React.Fragment>
                    );
                  })}
                </Flex>
              </Col>
            </Row>
          </Card>
        </div>

        {children}
      </Space>
    </div>
  );
};

export default PageWrapper;
